import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, ClipboardList } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO = "https://content.webfactorysite.co.uk/20260113092111-Wales-Event-Logo.png?t=1768296071";

const EMPTY = {
  first_name: "", middle_names: "", last_name: "", dob: "", phone: "", email: "",
  address: "", nhs_number: "", emergency_contact_name: "", emergency_contact_phone: "", allergies: "",
};

const inputCls =
  "w-full h-12 rounded-lg border border-slate-300 bg-white px-3 text-base focus:outline-none focus:ring-2 focus:ring-[#005EB8]";

export default function SelfCheckin() {
  const { token } = useParams();
  const [event, setEvent] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState("");
  const [fieldErr, setFieldErr] = useState("");

  useEffect(() => {
    fetch(`${API}/public/event-checkin/${token}`, { credentials: "omit" })
      .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
      .then((d) => setEvent(d))
      .catch(() => setError("This check-in link is not valid or has expired."));
  }, [token]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setFieldErr("");
    setSending(true);
    try {
      const r = await fetch(`${API}/public/event-checkin/${token}`, {
        method: "POST",
        credentials: "omit",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Could not check in — please try again.");
      setDone(d.message);
    } catch (err) {
      setFieldErr(err.message);
    } finally {
      setSending(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#0F2547] grid place-items-center p-6">
        <div className="bg-white rounded-2xl p-8 max-w-sm text-center" data-testid="checkin-error">
          <p className="font-heading font-bold text-[#0F2547] text-lg mb-2">Check-in unavailable</p>
          <p className="text-sm text-slate-500">{error}</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-[#F4F7FA] grid place-items-center">
        <div className="h-10 w-10 rounded-full border-4 border-[#005EB8] border-t-transparent animate-spin" />
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-[#F4F7FA] grid place-items-center p-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-8 max-w-md text-center shadow-sm" data-testid="checkin-success">
          <img src={LOGO} alt="Wales Event Medical Services" className="h-12 w-auto mx-auto mb-4" />
          <CheckCircle2 className="h-12 w-12 text-[#007F3B] mx-auto mb-3" />
          <p className="font-heading font-extrabold text-[#0F2547] text-xl mb-2">You're checked in</p>
          <p className="text-sm text-slate-600">{done}</p>
          <p className="text-xs text-slate-400 mt-4">{event.event}{event.venue && ` · ${event.venue}`}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F7FA]" data-testid="self-checkin-page">
      <header className="bg-[#0F2547] text-white px-5 py-5 text-center">
        <div className="bg-white rounded-lg p-2 inline-block mb-3">
          <img src={LOGO} alt="Wales Event Medical Services" className="h-10 w-auto" />
        </div>
        <h1 className="font-heading font-extrabold text-xl leading-tight" data-testid="checkin-event-name">{event.event}</h1>
        <p className="text-xs text-slate-300 mt-1">
          {event.venue}{event.venue && event.event_date && " · "}{event.event_date}
        </p>
        <p className="text-xs text-sky-300 font-semibold mt-2 uppercase tracking-widest">Patient self check-in</p>
      </header>

      <main className="max-w-lg mx-auto p-5">
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <p className="text-sm text-slate-600 mb-4 flex items-start gap-2">
            <ClipboardList className="h-4 w-4 mt-0.5 shrink-0 text-[#005EB8]" />
            Please fill in your details so our medical team can see you. It takes under a minute.
          </p>
          <form onSubmit={submit} className="space-y-3" data-testid="checkin-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-500 mb-1 block">First name *</label>
                <input required data-testid="checkin-first-name" className={inputCls} value={form.first_name} onChange={set("first_name")} />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 mb-1 block">Last name *</label>
                <input required data-testid="checkin-last-name" className={inputCls} value={form.last_name} onChange={set("last_name")} />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 mb-1 block">Middle name(s)</label>
              <input data-testid="checkin-middle-names" className={inputCls} value={form.middle_names} onChange={set("middle_names")} />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 mb-1 block">Date of birth *</label>
              <input required type="date" data-testid="checkin-dob" className={inputCls} value={form.dob} onChange={set("dob")} />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 mb-1 block">Mobile number *</label>
              <input required type="tel" data-testid="checkin-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="07…" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 mb-1 block">Email address *</label>
              <input required type="email" data-testid="checkin-email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@example.com" />
              <p className="text-[11px] text-slate-400 mt-1">So we can email your treatment record to you (or a parent/guardian) if needed.</p>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 mb-1 block">Home address</label>
              <input data-testid="checkin-address" className={inputCls} value={form.address} onChange={set("address")} />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 mb-1 block">Allergies</label>
              <input data-testid="checkin-allergies" className={inputCls} value={form.allergies} onChange={set("allergies")} placeholder="e.g. Penicillin, nuts — or leave blank" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 mb-1 block">NHS number (if known)</label>
              <input data-testid="checkin-nhs" className={inputCls} value={form.nhs_number} onChange={set("nhs_number")} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-500 mb-1 block">Emergency contact</label>
                <input data-testid="checkin-emergency-name" className={inputCls} value={form.emergency_contact_name} onChange={set("emergency_contact_name")} placeholder="Name" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 mb-1 block">&nbsp;</label>
                <input type="tel" data-testid="checkin-emergency-phone" className={inputCls} value={form.emergency_contact_phone} onChange={set("emergency_contact_phone")} placeholder="Phone" />
              </div>
            </div>
            {fieldErr && <p className="text-xs font-semibold text-[#D5281B]" data-testid="checkin-form-error">{fieldErr}</p>}
            <button type="submit" disabled={sending} data-testid="checkin-submit"
              className="w-full h-14 rounded-xl bg-[#005EB8] hover:bg-[#004a93] transition-colors text-white font-bold text-base disabled:opacity-60">
              {sending ? "Checking you in…" : "Check in"}
            </button>
            <p className="text-[11px] text-slate-400 text-center">Your details go straight to the medical team on site. Confidential.</p>
          </form>
        </div>
      </main>
    </div>
  );
}
